/**
 * TẦNG VẬN CHUYỂN — nơi DUY NHẤT trong PWA gọi `fetch` tới API chấm công.
 *
 * Hai ngoại lệ có chủ ý, cả hai đều đúng:
 *   · `lib/queue.ts` tự gọi `/attendance/check-in` vì nó phải gửi `FormData`
 *     kèm ảnh và phải kiểm soát chính xác việc thử lại của hàng đợi offline.
 *     Nó vẫn lấy token qua `layToken()` / `lamMoiToken()` ở tệp này.
 *   · `services/beacon.service.ts` gọi thẳng beacon trong LAN — đó là một máy
 *     chủ KHÁC, không phải API, và có quy tắc timeout riêng.
 *
 * Hợp đồng API: docs/05 §5.2 · mã lỗi §5.3.
 */

import { ApiError } from './loi'

/**
 * Địa chỉ gốc của API.
 *
 * Mặc định là đường dẫn TƯƠNG ĐỐI `/api/v1`, đúng với cách triển khai hiện
 * tại: nginx phục vụ PWA và API trên cùng một origin, nên trình duyệt tự ghép
 * đúng tên miền và cổng. Một bản build chạy được ở `localhost:8088`,
 * `192.168.1.110` lẫn `chamcong.dieutuongam.com`.
 *
 * `VITE_API_BASE_URL` chỉ cần khi tách origin. Nhớ rằng biến này nướng vào
 * bundle LÚC BUILD — đổi giá trị là phải `npm run build` lại.
 */
export const API_BASE: string = import.meta.env.VITE_API_BASE_URL || '/api/v1'

const KHOA_TOKEN = 'chamcong.access_token'
const KHOA_REFRESH = 'chamcong.refresh_token'
export const KHOA_NHAN_VIEN = 'chamcong.nhan_vien'
export const KHOA_FACE = 'chamcong.face_status'

export function layToken(): string | null {
  return localStorage.getItem(KHOA_TOKEN)
}

export function datToken(token: string | null): void {
  if (token) localStorage.setItem(KHOA_TOKEN, token)
  else localStorage.removeItem(KHOA_TOKEN)
}

export const layRefresh = () => localStorage.getItem(KHOA_REFRESH)

export function datRefresh(t: string | null): void {
  if (t) localStorage.setItem(KHOA_REFRESH, t)
  else localStorage.removeItem(KHOA_REFRESH)
}

export function xoaPhienDangNhap(): void {
  datToken(null)
  datRefresh(null)
  localStorage.removeItem(KHOA_NHAN_VIEN)
  localStorage.removeItem(KHOA_FACE)
  localStorage.removeItem('chamcong.ca_dang_mo')
}

/* ═══════════════════════════════════════════════════════════════════════════
 * MÓC NỐI LÊN VỎ ỨNG DỤNG
 * ═══════════════════════════════════════════════════════════════════════════ */

let baoPhaiDoiMatKhau: (() => void) | null = null

/**
 * Vỏ ứng dụng đăng ký ở đây để biết khi nào server bắt đổi mật khẩu.
 *
 * Vì sao bắt ở tầng chung thay vì từng màn hình: admin có thể đặt lại mật
 * khẩu trong lúc nhân viên đang mở app, và lời gọi API kế tiếp — bất kể màn
 * nào — sẽ trả 403 `PASSWORD_CHANGE_REQUIRED`. Bắt ở mỗi màn là chắc chắn sót
 * một chỗ, và chỗ sót đó hiện ra dưới dạng "Bạn không có quyền" khó hiểu.
 */
export function dangKyXuLyBatBuocDoiMatKhau(fn: () => void): () => void {
  baoPhaiDoiMatKhau = fn
  return () => { baoPhaiDoiMatKhau = null }
}

let baoMatPhien: (() => void) | null = null

/**
 * Refresh token hết hạn (30 ngày) hoặc bị thu hồi — phải đăng nhập lại.
 *
 * ⚠️ Vỏ ứng dụng KHÔNG được xoá hàng đợi offline khi nhận tín hiệu này. Trong
 * hàng đợi có thể đang nằm giờ công thật của người đó, và nó gửi được ngay
 * khi họ đăng nhập lại.
 */
export function dangKyXuLyMatPhien(fn: () => void): () => void {
  baoMatPhien = fn
  return () => { baoMatPhien = null }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LÀM MỚI TOKEN
 *
 * Access token chỉ sống 15 phút. Không làm mới thì nhân viên bị đá ra đăng
 * nhập lại giữa ca — trải nghiệm tệ và không cần thiết vì refresh sống 30 ngày.
 * ═══════════════════════════════════════════════════════════════════════════ */

async function goiLamMoi(): Promise<boolean> {
  const rt = layRefresh()
  if (!rt) return false
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    })
    if (!res.ok) return false
    const kq = (await res.json()) as { access_token: string; refresh_token: string }
    datToken(kq.access_token)
    datRefresh(kq.refresh_token)
    return true
  } catch {
    return false
  }
}

/*
 * Gom mọi lời gọi làm mới đang chờ vào MỘT lời hứa.
 *
 * Quan trọng hơn ở PWA so với trang quản trị: khi có sóng trở lại, hàng đợi
 * offline đẩy nhiều bản ghi cùng lúc và cùng gặp token hết hạn. Server xoay
 * vòng refresh token, nên nếu năm lời gọi cùng làm mới thì bốn cái sau dùng
 * token đã bị thu hồi ⇒ nhân viên bị đăng xuất oan GIỮA LÚC đang đồng bộ giờ
 * công.
 */
let dangLamMoi: Promise<boolean> | null = null

export function lamMoiToken(): Promise<boolean> {
  dangLamMoi ??= goiLamMoi().finally(() => { dangLamMoi = null })
  return dangLamMoi
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LỜI GỌI JSON
 * ═══════════════════════════════════════════════════════════════════════════ */

export async function request<T>(
  path: string, init: RequestInit = {}, thuLai = true,
): Promise<T> {
  const headers = new Headers(init.headers)
  const token = layToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  const text = await res.text()

  // Không phải phản hồi nào cũng là JSON: nginx trả TRANG HTML cho 429 (vượt
  // giới hạn tần suất), 502/504 (API chết hoặc quá hạn). `JSON.parse` ném
  // SyntaxError, và trước đây màn hình đăng nhập bắt trượt nó thành "Không
  // kết nối được máy chủ. Kiểm tra mạng" — đổ oan cho mạng trong khi mạng vẫn
  // tốt. Ở công trường, chẩn đoán sai một câu là mất nửa buổi.
  let body: Record<string, unknown> = {}
  if (text) {
    try {
      body = JSON.parse(text) as Record<string, unknown>
    } catch {
      throw new ApiError(
        `HTTP_${res.status}`,
        res.status === 429
          ? 'Máy chủ đang giới hạn số lần thử. Chờ khoảng một phút rồi thử lại.'
          : res.status >= 500
            ? `Máy chủ đang lỗi (mã ${res.status}). Báo quản trị, không phải lỗi máy bạn.`
            : `Máy chủ trả về phản hồi không đọc được (mã ${res.status}).`,
        res.status,
        { than_phan_hoi: text.slice(0, 200) },
      )
    }
  }

  if (!res.ok) {
    const err = (body?.error ?? {}) as Record<string, unknown>

    if (res.status === 403 && err.code === 'FORBIDDEN' && thuLai) {
      if (await lamMoiToken()) return request<T>(path, init, false)
      if (layRefresh()) { xoaPhienDangNhap(); baoMatPhien?.() }
    }

    // Trừ chính endpoint đổi mật khẩu: ở đó 403 nghĩa là gõ sai mật khẩu cũ,
    // chuyển màn khi đang đứng đúng màn đó là một vòng lặp vô nghĩa.
    if (err.code === 'PASSWORD_CHANGE_REQUIRED'
        && !path.startsWith('/auth/change-password')) {
      baoPhaiDoiMatKhau?.()
    }

    throw new ApiError(
      (err.code as string) ?? `HTTP_${res.status}`,
      (err.message as string) ?? 'Có lỗi xảy ra, thử lại sau',
      res.status,
      (err.detail as Record<string, unknown>) ?? {},
    )
  }
  return body as T
}
